<template>
  <div id="chart" class="chart-wrapper">
      <div class="toolbar">
          <button @click="updateData(100)"  :class="{active: selection==='100'}">100</button>
          <button @click="updateData(500)"  :class="{active: selection==='500'}">500</button>
          <button @click="updateData(1000)" :class="{active: selection==='1000'}">1000</button>
      </div>

      <apexchart type="bar" height="90%" ref="chart" :options="manualsOptions" :series="manualsDensity"></apexchart>
      <p class="align-center mt-10">PMP: {{ kpis.portfolioPMPManuals }} | TotalQty: {{ kpis.portfolioVolumeManuals }} | Open P/L: {{ kpisDataCalcs.plManuals.toFixed(2) }} | Allocated Capital: $ {{ kpisDataCalcs.allocatedCapitalManuals.toFixed(2) || 0 }}</p>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { densityUnitsOptions } from '@/utils/globalApexConfig';
import { calcPL } from '@/utils/funcs.js';
import Big from 'big.js';

export default {
  name: 'manualsDensity',
  data() {
    return {
      interval: 1000,
      selection: '1000',
    }
  },
  computed: {
    ...mapState({ kpis: state => state.kpis, marketData: state => state.marketData }),
    ...mapGetters(['manualDensityByBin', 'kpisDataCalcs']),

    // ─── Tier 1: cached until ledger or interval changes ─────────────────────
    costStructure() {
      return this.manualDensityByBin(this.interval);
    },

    // ─── Tier 2: only P/L loop re-runs on price ticks ────────────────────────
    densityData() {
      const bins = this.costStructure;
      const marketPrice = Number(this.marketData.btcUsd ?? 0);

      return Object.entries(bins)
        .map(([bin, { qty, units }]) => {
          const pl = units.reduce((acc, u) => {
            const avgPrice = Number(u.current?.avgPrice ?? 0);
            const uQty     = Number(u.current?.qty ?? 0);
            if (Number.isNaN(avgPrice) || Number.isNaN(uQty)) return acc;
            return acc.plus(new Big(calcPL(marketPrice, avgPrice, uQty)));
          }, new Big(0));
          return {
            x:  Number(bin).toFixed(2),
            y:  parseFloat(new Big(qty).toFixed(5)),
            y2: parseFloat(pl.toFixed(2)),
          };
        })
        .sort((a, b) => parseFloat(a.x) - parseFloat(b.x));
    },

    manualsDensity() {
      const labels = this.densityData.map(p => p.x);
      return [
        {
          name: 'Manuals',
          type: 'bar',
          data:   this.densityData.map(p => p.y),
          labels,
        },
        {
          name: 'P/L',
          type: 'line',
          data: this.densityData.map(p => p.y2),
        }
      ];
    },

    manualsOptions() {
      return {
        ...densityUnitsOptions,
        xaxis: {
          type: 'category',
          categories: this.densityData.map(p => p.x),
        },
        yaxis: [
          { title: { text: 'Quantity' }, show: false },
          {
            title: { text: 'Open P/L' },
            opposite: true,
            show: false,
          }
        ],
        tooltip: {
          shared: true,
          intersect: false,
        }
      };
    },
  },
  methods: {
    updateData(interval) {
      this.selection = `${interval}`;
      this.interval = interval;
    }
  }
}
</script>

<style scoped>
.active {
    background-color: #333 !important;
    color: #fff !important;
}

.toolbar button {
  background-color: aliceblue;
  width: 30px;
  height: 20px;
  color: #333;
  margin-right: 4px;
  font-size: 8px !important;
  padding: 0 4px !important;
}
</style>
